import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, ShieldCheck, MessageCircle, Mail, Zap } from "lucide-react";
import { RESERVE_INCLUDES, RESERVE_TRUST, JOURNEY, BRAND } from "@/constants/content";
import { HOME } from "@/constants/testIds";
import { useReserve } from "@/components/boost/ReserveContext";

const TRUST_ICONS = [ShieldCheck, Zap, MessageCircle, Mail];

const Reserve = () => {
  const { open } = useReserve();
  return (
    <section id="reserve" className="relative overflow-hidden text-white">
      {/* Backgrounds */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(80% 60% at 20% 0%, rgba(16,185,129,0.35), transparent 60%), radial-gradient(60% 60% at 100% 100%, rgba(163,230,53,0.28), transparent 60%), linear-gradient(180deg, #04120f, #0a0a0a)",
        }}
      />
      <div className="aurora opacity-90" aria-hidden />
      <div className="grain" aria-hidden />
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse at 50% 30%, black 40%, transparent 70%)",
          WebkitMaskImage: "radial-gradient(ellipse at 50% 30%, black 40%, transparent 70%)",
        }}
      />

      <div className="container-x section-pad relative">
        <div className="max-w-3xl">
          <div className="chip-dark">Reserve Your Seat Today</div>
          <h2
            className="mt-4 font-display font-extrabold tracking-tight leading-[0.95]"
            style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}
          >
            Your journey toward your first <span className="gradient-text-lime">successful eCommerce business</span> starts here.
          </h2>
          <p className="mt-5 text-white/70 max-w-xl">
            One small step, one committed community, one clear path — from empty screen to a live store with real orders.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-[1.15fr_1fr] gap-6 items-start">
          {/* Pricing glass card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-15% 0px" }}
            transition={{ duration: 0.6 }}
            className="relative rounded-[32px] glass-dark p-6 sm:p-10 border border-white/10 shadow-emerald-glow overflow-hidden"
          >
            <div
              aria-hidden
              className="absolute -inset-24 opacity-70"
              style={{
                background:
                  "radial-gradient(45% 45% at 20% 20%, rgba(16,185,129,0.35), transparent 60%), radial-gradient(35% 35% at 90% 90%, rgba(163,230,53,0.35), transparent 60%)",
                filter: "blur(20px)",
              }}
            />

            <div className="relative z-10">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-lime-300">BoostF eCommerce Launch Marathon</div>
                  <div className="mt-2 flex items-baseline gap-2">
                    <div className="font-display font-extrabold text-6xl sm:text-7xl gradient-text-lime" style={{ letterSpacing: "-0.03em" }}>
                      ₹{BRAND.price}
                    </div>
                    <div className="text-white/60 text-sm">/ one-time</div>
                  </div>
                </div>
                <div className="chip-dark">Limited seats</div>
              </div>

              <ul className="mt-8 grid gap-2 sm:grid-cols-2">
                {RESERVE_INCLUDES.map((i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-white/90">
                    <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-300 grid place-items-center">
                      <Check className="w-3 h-3" />
                    </span>
                    {i}
                  </li>
                ))}
              </ul>

              {/* Huge glowing CTA */}
              <div className="mt-8 relative">
                <div
                  aria-hidden
                  className="absolute -inset-3 rounded-full opacity-60 blur-2xl"
                  style={{
                    background:
                      "radial-gradient(50% 50% at 50% 50%, rgba(163,230,53,0.55), transparent 70%)",
                  }}
                />
                <button
                  onClick={open}
                  data-testid={HOME.reserveSectionCta}
                  className="relative btn-primary w-full sm:w-auto h-16 px-8 text-lg group"
                  style={{
                    background: "linear-gradient(135deg, #10b981, #a3e635)",
                    boxShadow:
                      "0 0 0 1px rgba(163,230,53,0.4), 0 20px 60px -10px rgba(163,230,53,0.5), 0 0 100px -10px rgba(16,185,129,0.6)",
                  }}
                >
                  Reserve My Seat
                  <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>

              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2">
                {RESERVE_TRUST.map((t, i) => {
                  const Icon = TRUST_ICONS[i];
                  return (
                    <div key={t} className="flex items-center gap-2 text-xs text-white/70 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                      <Icon className="w-3.5 h-3.5 text-lime-300 shrink-0" />
                      {t}
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Journey timeline */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-15% 0px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative rounded-[32px] glass-dark p-6 sm:p-8 border border-white/10 overflow-hidden"
          >
            <div className="text-[11px] font-mono uppercase tracking-widest text-lime-300">
              Your journey · in 7 steps
            </div>
            <ol className="mt-5 relative">
              <div className="absolute left-4 top-2 bottom-2 w-px bg-gradient-to-b from-emerald-400/50 via-emerald-400/30 to-lime-300/40" />
              {JOURNEY.map((j, i) => (
                <motion.li
                  key={j}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="relative pl-12 py-2.5"
                >
                  <div
                    className={`absolute left-0 top-2.5 w-8 h-8 rounded-full grid place-items-center text-xs font-display font-bold ${
                      i === JOURNEY.length - 1
                        ? "bg-gradient-to-br from-lime-400 to-emerald-500 text-black shadow-lime-glow"
                        : "bg-white/10 border border-white/20 text-white"
                    }`}
                  >
                    {i + 1}
                  </div>
                  <div className="font-display font-semibold text-white text-base leading-tight">{j}</div>
                </motion.li>
              ))}
            </ol>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Reserve;
